import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaPredicate } from 'react-media-hook';
import { BarChart, Bar, YAxis, ResponsiveContainer } from 'recharts';

import { HealthReadingData } from 'services/face-scan/type';

interface CustomProps {
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
}

export default function ScanChart({
  selectedIndex,
  setSelectedIndex,
  scans,
}: {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  scans: HealthReadingData[];
}) {
  const { t } = useTranslation();
  const isMediaTab = useMediaPredicate('(max-width: 1023px)');

  const handleBarClick = (index: number) => {
    setSelectedIndex(index);
  };

  const CustomBar = ({ x, y, width, height, index }: CustomProps) => {
    const topBarHeight = 4;
    const borderRadius = 2;
    const normalColor = '#FFFFFF00';
    const selectedColor = '#10A4B11A';

    const isSelected = index === selectedIndex;

    return (
      <g onClick={() => setSelectedIndex(index)} style={{ cursor: 'pointer' }}>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={isSelected ? selectedColor : normalColor}
          rx={10}
          ry={10}
        />

        <rect
          x={x}
          y={y}
          width={width}
          height={topBarHeight}
          rx={borderRadius}
          ry={borderRadius}
          fill='#C6E03E'
        />

        <text
          x={x + width / 2}
          y={y + height - 8}
          textAnchor='middle'
          fill='#fff'
          fontSize={11}
          fontFamily='Isidora Sans'
        >
          {isMediaTab ? data[index].date : data[index].name}
        </text>
      </g>
    );
  };

  const CustomBackground = ({ x, y, width, height, index }: CustomProps) => {
    const isSelected = index === selectedIndex;

    return (
      <g onClick={() => handleBarClick(index)} style={{ cursor: 'pointer' }}>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill='transparent'
          onClick={(e) => e.stopPropagation()}
        />

        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={isSelected ? '#10A4B133' : '#FFFFFF1A'}
          rx={10}
          ry={10}
        />
      </g>
    );
  };

  const data = useMemo(
    () =>
      scans.length
        ? scans.map((reading) => ({
          name: format(parseISO(reading.created_at), 'do MMM'),
          value: +reading.medsi_wellness_score,
          date: format(parseISO(reading.created_at), 'dd/MM'),
        }))
        : [],
    [scans]
  );
  return (
    <div
      id='scan-scores'
      className='flex flex-col w-full h-full col-span-1 p-4 min-h-44 sm:col-span-6 lg:col-span-5 bg-primary-dark rounded-2xl gap-y-2'
    >
      <div className='shrink-0'>
        <h1 className='font-semibold text-white'>{t('scan_overs_time')}</h1>
      </div>

      <div className='relative flex-1 min-h-[140px] w-full'>
        <div className='absolute inset-0'>
          <ResponsiveContainer width='100%' height='100%' minHeight={140}>
            <BarChart data={data} margin={{ left: -30, top: 8 }}>
              <YAxis
                tickLine={false}
                axisLine={false}
                domain={[0, 100]}
                ticks={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                interval={0}
                tick={{ fontSize: 12, fontFamily: 'Isidora Sans', fill: '#fff' }}
              />
              <Bar
                dataKey='value'
                isAnimationActive={false}
                shape={(props: any) => (
                  <CustomBar {...props} index={props.index} />
                )}
                // @ts-expect-error no error
                background={<CustomBackground />}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
